import React from 'react';
import { useNavigate } from 'react-router';
import { Shield, TrendingUp, Users, Download } from 'lucide-react';
import bg_image from '../assets/images/bg_imageee.jpg'
import PublicLayout from '../components/PublicLayout';

export default function LandingPage() {
    const navigate = useNavigate();

    return (
        <PublicLayout>
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 lg:pt-56 lg:pb-40 overflow-hidden">
                {/* Background Image & Overlay */}
                <div className="absolute inset-0 w-full h-full z-0">
                    <img
                        src={bg_image}
                        alt="Restaurant Ambience"
                        className="w-full h-full object-cover scale-105 animate-pulse-slow"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-premium-dark/80 via-premium-dark/40 to-premium-dark"></div>
                    <div className="absolute inset-0 bg-premium-teal/20 mix-blend-overlay"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-premium-gold/30 bg-premium-gold/10 backdrop-blur-sm animate-fade-in-up">
                        <span className="text-premium-gold-light text-sm font-semibold tracking-wide uppercase">New Experience in Food Delivery</span>
                    </div>
                    <h1 className="text-6xl md:text-8xl font-black text-white tracking-tight mb-8 animate-fade-in-up leading-[1.1]">
                        Taste the <br />
                        <span className="premium-gradient-text">
                            Extraordinary
                        </span>
                    </h1>
                    <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-300 mb-12 leading-relaxed animate-fade-in-up [animation-delay:200ms]">
                        Indulge in a curated selection of top-rated restaurants, delivered with precision and speed by our elite Odaz drivers.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-6 animate-fade-in-up [animation-delay:400ms]">
                        <button
                            className="premium-button px-10 py-5 bg-premium-gold text-premium-teal font-black rounded-full shadow-2xl shadow-premium-gold/30 flex items-center justify-center gap-3 group"
                        >
                            Download our App
                            <Download className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
                        </button>
                        <button className="premium-button px-10 py-5 bg-white/5 border border-white/10 text-white font-bold rounded-full hover:bg-white/10 transition-all duration-500 backdrop-blur-md">
                            Work with Us
                        </button>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[120%] h-64 bg-premium-dark blur-3xl rounded-[100%] z-0"></div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-32 bg-premium-dark relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-24">
                        <h2 className="text-premium-gold font-black tracking-[0.2em] uppercase mb-4 animate-glow">Features</h2>
                        <p className="mt-2 text-4xl md:text-6xl font-black tracking-tighter text-white">
                            Beyond Just Delivery.
                        </p>
                        <div className="w-24 h-1.5 bg-premium-gold mx-auto mt-8 rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {/* Feature 1 */}
                        <div className="group p-10 rounded-[2.5rem] bg-white/[0.03] border border-white/10 hover:border-premium-gold/50 transition-all duration-700 hover:bg-premium-gold/[0.02] relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-premium-gold opacity-0 group-hover:opacity-10 blur-3xl transition-opacity"></div>
                            <div className="w-16 h-16 bg-premium-gold/10 rounded-2xl flex items-center justify-center mb-8 rotate-3 group-hover:rotate-12 transition-transform duration-500">
                                <Users className="w-8 h-8 text-premium-gold" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Elite Variety</h3>
                            <p className="text-gray-400 leading-relaxed">Discover an exclusive collection of high-end eateries and local hidden gems, handpicked for you.</p>
                        </div>

                        {/* Feature 2 */}
                        <div className="group p-10 rounded-[2.5rem] bg-white/[0.03] border border-white/10 hover:border-premium-gold/50 transition-all duration-700 hover:bg-premium-gold/[0.02] relative overflow-hidden transform md:scale-110 md:-translate-y-4 shadow-2xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-premium-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="w-16 h-16 bg-premium-gold/10 rounded-2xl flex items-center justify-center mb-8 -rotate-3 group-hover:-rotate-12 transition-transform duration-500">
                                <TrendingUp className="w-8 h-8 text-premium-gold" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Precision Logistics</h3>
                            <p className="text-gray-400 leading-relaxed">Our advanced routing ensures your culinary masterpiece arrives exactly as the chef intended.</p>
                        </div>

                        {/* Feature 3 */}
                        <div className="group p-10 rounded-[2.5rem] bg-white/[0.03] border border-white/10 hover:border-premium-gold/50 transition-all duration-700 hover:bg-premium-gold/[0.02] relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-premium-gold opacity-0 group-hover:opacity-10 blur-3xl transition-opacity"></div>
                            <div className="w-16 h-16 bg-premium-gold/10 rounded-2xl flex items-center justify-center mb-8 rotate-3 group-hover:rotate-12 transition-transform duration-500">
                                <Shield className="w-8 h-8 text-premium-gold" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Secure Flow</h3>
                            <p className="text-gray-400 leading-relaxed">Top-tier encryption ensures your transactions are as smooth and safe as your delivery experience.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Us Section */}
            <section id="about" className="py-32 bg-premium-teal/10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-premium-gold/30 to-transparent"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="relative">
                            <div className="absolute -top-20 -left-20 w-64 h-64 bg-premium-gold/10 rounded-full blur-3xl animate-pulse-slow"></div>
                            <h2 className="text-premium-gold font-black tracking-[0.2em] uppercase mb-6">Our Story</h2>
                            <h3 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
                                Redefining the <br />
                                <span className="premium-gradient-text">Delivery Standards</span>
                            </h3>
                            <div className="space-y-8 text-gray-300 text-lg leading-relaxed font-light">
                                <p>
                                    Odaz was designed to make daily living easier and more convenient. Through a quick, reliable, easily accessible and dependable digital platform, we connect people with products and services they need.
                                </p>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-px w-8 bg-premium-gold"></div>
                                        <h4 className="text-premium-gold font-black tracking-widest uppercase text-sm">Mission</h4>
                                    </div>
                                    <p>To link individuals with the goods and services they require by offering delivery services that are quick, dependable, and easily accessible.</p>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-px w-8 bg-premium-gold"></div>
                                        <h4 className="text-premium-gold font-black tracking-widest uppercase text-sm">Vision</h4>
                                    </div>
                                    <p>To become a leading platform that transforms everyday convenience and supports local communities through innovation and technology.</p>
                                </div>
                            </div>
                        </div>
                        <div className="relative group">
                            <div className="absolute inset-0 bg-premium-gold rounded-[3rem] rotate-3 scale-95 opacity-20 group-hover:rotate-6 transition-transform duration-700"></div>
                            <div className="relative rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl">
                                <img
                                    src={bg_image}
                                    alt="Our Team"
                                    className="w-full h-[600px] object-cover hover:scale-110 transition-transform duration-1000"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-premium-dark via-transparent to-transparent opacity-60"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-premium-teal py-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-premium-dark/20 backdrop-blur-[2px]"></div>
                <div className="absolute top-[-50%] right-[-10%] w-[1000px] h-[1000px] bg-premium-gold/5 rounded-full blur-[120px] animate-pulse-slow"></div>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter">Ready to Begin?</h2>
                    <p className="text-teal-100/70 text-xl mb-12 max-w-2xl mx-auto leading-relaxed font-light">Join an elite community of gourmands who settle for nothing but the best. Your next extraordinary meal is a click away.</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="premium-button bg-premium-gold text-premium-teal px-12 py-5 rounded-full font-black text-xl hover:bg-premium-gold-light shadow-[0_20px_50px_rgba(212,175,55,0.3)] transition-all"
                    >
                        Join the Experience
                    </button>
                </div>
            </section>
        </PublicLayout>
    );
}
